import { semanticTokens } from '@supabank/config/theme'
import * as React from 'react'
import { FOCUS_ORDER, getFocusAdvance } from '../focus/focusOrder'
import { useFocusVisible } from '../focus/useFocusVisible'
import { cn } from '../utils'
import { Button } from './button'
import { SkipToContent } from './skip-to-content'

export interface PageProps extends React.HTMLAttributes<HTMLDivElement> {
  'data-ux-contract'?: 'canon'
  skipToTable?: boolean
}

/**
 * Page - Root container for Page Contract layout
 * 
 * @example
 * <Page data-ux-contract="canon">
 *   <Page.Header icon={ShieldIcon} title="Customers" />
 *   <Page.Toolbar items={toolbarItems} />
 *   <Page.StatGrid items={stats} />
 *   <Page.Content>Content</Page.Content>
 * </Page>
 */
export function Page({ className, 'data-ux-contract': dataUxContract = 'canon', skipToTable = false, ...props }: PageProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const focusVisible = useFocusVisible()

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return

    return getFocusAdvance(container, FOCUS_ORDER)
  }, [])

  return (
    <>
      <SkipToContent skipToTable={skipToTable} />
      <div
        ref={containerRef}
        data-ux-contract={dataUxContract}
        id="main-content"
        className={cn(
          'min-h-screen',
          semanticTokens.bg.surface,
          semanticTokens.motion.fadeIn,
          semanticTokens.motion.reduced,
          focusVisible.className,
          // Default padding if not provided via className
          !className?.includes('p-') && 'p-4 lg:p-6',
          className
        )}
        data-focus-visible={focusVisible['data-focus-visible']}
        data-focus-zone="page"
        {...props}
      />
    </>
  )
}

// Page.Header
export interface PageHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ComponentType<{ className?: string }>
  title: string
  subtitle?: string
  badge?: string
  actions?: React.ReactNode // Actions buttons on the right (like BalanceCard)
  showBackButton?: boolean // Show back button on mobile (<lg breakpoint)
  backHref?: string // URL to navigate to when back button is clicked (default: '/dashboard')
  onBack?: () => void // Callback when back button is clicked (takes precedence over backHref)
}

export function PageHeader({
  icon: Icon,
  title,
  subtitle,
  badge,
  actions,
  showBackButton = false,
  backHref = '/dashboard',
  onBack,
  className,
  ...props
}: PageHeaderProps) {
  const handleBack = React.useCallback(() => {
    if (onBack) {
      onBack()
    } else if (backHref && typeof window !== 'undefined') {
      // Try to use history API for SPA navigation, fallback to full reload
      if (window.history && window.history.pushState) {
        window.history.pushState({}, '', backHref)
        window.dispatchEvent(new PopStateEvent('popstate'))
      } else {
        window.location.href = backHref
      }
    }
  }, [onBack, backHref])

  return (
    <header
      className={cn(
        'mb-8',
        semanticTokens.motion.slideDown,
        semanticTokens.motion.reduced,
        className
      )}
      {...props}
    >
      {/* Mobile Back Button - shown only on <lg breakpoint */}
      {showBackButton && (
        <div className="mb-4 lg:hidden">
          <Button
            variant="subtle"
            size="sm"
            onClick={handleBack}
            className={cn(
              "min-h-[44px] gap-2",
              semanticTokens.motion.standard,
              semanticTokens.focus.ring
            )}
            aria-label="Back to dashboard"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span>Back</span>
          </Button>
        </div>
      )}

      <div className={cn(
        "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4",
        semanticTokens.spacing.md.replace('p-', 'gap-')
      )}>
        <div className="flex items-start gap-4">
          {Icon && (
            <div className={cn('mt-1', semanticTokens.fg.muted)}>
              <Icon className="h-6 w-6" />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className={cn(semanticTokens.typography.h1, semanticTokens.fg.default)}>
                {title}
              </h1>
              {badge && (
                <span
                  className={cn(
                    'px-2 py-0.5 text-xs font-medium',
                    semanticTokens.radius.md,
                    semanticTokens.info.bg,
                    semanticTokens.info.fg
                  )}
                >
                  {badge}
                </span>
              )}
            </div>
            {subtitle && (
              <p className={cn('mt-2', semanticTokens.typography.body, semanticTokens.fg.muted)}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Actions on the right - matching BalanceCard pattern */}
        {actions && (
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {actions}
          </div>
        )}
      </div>
    </header>
  )
}

// Page.Toolbar
export type ToolbarItem =
  | { id: 'search'; hotkey: 'K'; ariaLabel: string; placeholder?: string }
  | { id: 'refresh'; hotkey: 'R'; ariaLabel: string }
  | { id: 'filters'; hotkey: 'F'; ariaLabel: string }
  | { id: 'export'; hotkey: 'E'; ariaLabel: string }
  | { id: 'primary'; hotkey: 'N'; ariaLabel: string; label: string }

export const TOOLBAR_CANON: ToolbarItem[] = [
  { id: 'search', hotkey: 'K', ariaLabel: 'Search', placeholder: 'Search...' },
  { id: 'refresh', hotkey: 'R', ariaLabel: 'Refresh' },
  { id: 'filters', hotkey: 'F', ariaLabel: 'Filters' },
  { id: 'export', hotkey: 'E', ariaLabel: 'Export' },
  { id: 'primary', hotkey: 'N', ariaLabel: 'Primary action', label: 'Create New' },
]

export interface PageToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
  items: ToolbarItem[]
  onAction?: (itemId: ToolbarItem['id']) => void
}

export function PageToolbar({ items, onAction, className, ...props }: PageToolbarProps) {
  // Enforce canonical order: Search → Refresh → Filters → Export → Primary
  const orderedItems = items.sort((a, b) => {
    const order = ['search', 'refresh', 'filters', 'export', 'primary']
    return order.indexOf(a.id) - order.indexOf(b.id)
  })

  return (
    <nav
      role="toolbar"
      aria-label="Page actions"
      className={cn(
        'mb-6 flex items-center gap-2',
        semanticTokens.motion.slideDown,
        semanticTokens.motion.reduced,
        className
      )}
      {...props}
    >
      {orderedItems.map((item, index) => {
        const delay = index * 40 // Stagger delay: 0ms, 40ms, 80ms, 120ms, 160ms
        return (
          <div
            key={item.id}
            className={cn('transition-all duration-240 ease-standard')}
            style={{ animationDelay: `${delay}ms` }}
          >
            {item.id === 'search' && (
              <input
                type="search"
                placeholder={item.placeholder}
                aria-label={item.ariaLabel}
                className={cn(
                  'h-10 rounded-md border px-3 text-sm',
                  semanticTokens.bg.surface,
                  semanticTokens.border.default,
                  semanticTokens.radius.md,
                  'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  semanticTokens.motion.micro,
                  semanticTokens.motion.reduced
                )}
              />
            )}
            {item.id === 'refresh' && (
              <button
                type="button"
                aria-label={item.ariaLabel}
                onClick={() => onAction?.('refresh')}
                className={cn(
                  'h-10 rounded-md border px-3 text-sm',
                  semanticTokens.bg.surface,
                  semanticTokens.border.default,
                  semanticTokens.radius.md,
                  'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  semanticTokens.motion.micro,
                  semanticTokens.motion.reduced
                )}
              >
                Refresh
              </button>
            )}
            {item.id === 'filters' && (
              <button
                type="button"
                aria-label={item.ariaLabel}
                onClick={() => onAction?.('filters')}
                className={cn(
                  'h-10 rounded-md border px-3 text-sm',
                  semanticTokens.bg.surface,
                  semanticTokens.border.default,
                  semanticTokens.radius.md,
                  'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  semanticTokens.motion.micro,
                  semanticTokens.motion.reduced
                )}
              >
                Filters
              </button>
            )}
            {item.id === 'export' && (
              <button
                type="button"
                aria-label={item.ariaLabel}
                onClick={() => onAction?.('export')}
                className={cn(
                  'h-10 rounded-md border px-3 text-sm',
                  semanticTokens.bg.surface,
                  semanticTokens.border.default,
                  semanticTokens.radius.md,
                  'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  semanticTokens.motion.micro,
                  semanticTokens.motion.reduced
                )}
              >
                Export
              </button>
            )}
            {item.id === 'primary' && (
              <button
                type="button"
                aria-label={item.ariaLabel}
                onClick={() => onAction?.('primary')}
                className={cn(
                  'h-10 rounded-md px-4 text-sm font-medium text-white',
                  semanticTokens.accent.primary,
                  semanticTokens.radius.md,
                  'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  semanticTokens.motion.micro,
                  semanticTokens.motion.reduced
                )}
              >
                {item.label}
              </button>
            )}
          </div>
        )
      })}
    </nav>
  )
}

// Page.StatGrid
export interface StatCardItem {
  label: string
  value: string | number
  delta?: { value: number; trend: 'up' | 'down' | 'neutral' }
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info'
}

export interface PageStatGridProps extends React.HTMLAttributes<HTMLDivElement> {
  items: StatCardItem[]
}

export function PageStatGrid({ items, className, ...props }: PageStatGridProps) {
  // Enforce 4-8 cards rule
  const displayItems = items.slice(0, 8)

  return (
    <div
      className={cn(
        'mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4',
        className
      )}
      {...props}
    >
      {displayItems.map((item, index) => {
        const delay = index * 40 // Stagger delay: 0ms, 40ms, 80ms, 120ms...
        return (
          <div
            key={index}
            className={cn(
              'rounded-xl border p-6',
              semanticTokens.bg.surface,
              semanticTokens.border.default,
              semanticTokens.radius.xl,
              semanticTokens.shadow.sm,
              semanticTokens.motion.fadeIn,
              semanticTokens.motion.reduced
            )}
            style={{ animationDelay: `${delay}ms` }}
          >
            <div className={cn('text-sm', semanticTokens.fg.muted)}>{item.label}</div>
            <div className={cn('mt-2 text-2xl font-semibold', semanticTokens.fg.default)}>
              {item.value}
            </div>
            {item.delta && (
              <div
                className={cn(
                  'mt-2 text-xs',
                  item.delta.trend === 'up' ? semanticTokens.positive.accent : semanticTokens.fg.muted
                )}
              >
                {item.delta.trend === 'up' ? '↑' : '↓'} {Math.abs(item.delta.value)}%
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Page.Content
export interface PageContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export function PageContent({ className, children, ...props }: PageContentProps) {
  return (
    <main
      id="main"
      className={cn(
        'space-y-6',
        semanticTokens.motion.fadeIn,
        semanticTokens.motion.reduced,
        className
      )}
      {...props}
    >
      {children}
    </main>
  )
}

// Attach subcomponents
Page.Header = PageHeader
Page.Toolbar = PageToolbar
Page.StatGrid = PageStatGrid
Page.Content = PageContent

